/**
 * RPC API Wrapper for Supabase
 * 
 * This module provides a clean API for interacting with Supabase RPC functions.
 * It handles error handling and data transformation.
 */

import { supabase } from './supabase';

/**
 * Generic RPC caller function
 * @param {string} name - The name of the RPC function to call
 * @param {object} params - Parameters to pass to the RPC function
 * @returns {Promise<any>} The data returned from the RPC call
 */
async function rpc(name, params) {
  const { data, error } = await supabase.rpc(name, params);
  if (error) {
    console.error('RPC error', name, params, error);
    const message = String(error.message ?? 'Unknown error');
    throw new Error(message);
  }
  return data;
}

/**
 * API object containing all RPC function wrappers
 */
export const api = {
  /**
   * List records from telegrambad with pagination and filters
   */
  listRecords: (args) => rpc('get_telegrambad_page_pairs', {
    p_search: args.search ?? null,
    p_archived: args.archived ?? null,
    p_with_dupes_only: !!args.withDupesOnly,
    p_limit: Number(args.limit ?? 50),
    p_offset: Number(args.offset ?? 0),
    p_pairs_thr: Number(args.pairsThr ?? 0.86)
  }),

  /**
   * Get a cluster of related records
   */
  getCluster: (id) =>
    rpc('get_record_cluster_telegrambad', { p_id: id }),

  /**
   * Get similar pairs for a record
   */
  getSimilarPairs: (args) =>
    rpc('get_record_similar_pairs_telegrambad', {
      p_id: args.id,
      p_thr: Number(args.thr ?? 0.86),
      p_limit: Number(args.limit ?? 50),
      p_offset: Number(args.offset ?? 0)
    }),

  /**
   * Send a record to telegram (OK action)
   */
  sendOk: (args) => rpc('send_to_telegrambad', {
    p_id: args.id,
    p_archive_source: args.archiveSource ?? true,
    p_allow_archived: args.allowArchived ?? false,
    p_metadata_extra: args.metadataExtra ?? {},
    p_dedupe_by_content: args.dedupeByContent ?? false
  }),

  /**
   * Archive a record (NOT OK action)
   */
  setNotOk: (id, reason) =>
    rpc('archive_telegrambad_record', { p_id: id, p_reason: reason }),

  /**
   * Edit record content
   */
  editRecord: (args) =>
    rpc('edit_telegrambad_content', { 
      p_id: args.id, 
      p_new_content: args.newContent, 
      p_metadata_patch: args.metadataPatch 
    }),

  /**
   * Set canonical record for duplicates
   */
  setCanonical: (args) =>
    rpc('set_canonical_telegrambad', { 
      p_duplicate_id: args.duplicateId, 
      p_canonical_id: args.canonicalId 
    })
};
